/* ═══════════════════════════════════════════════════════
   CrewCardPage — public route  /card/{token}[?r={recipientKey}]
   ═══════════════════════════════════════════════════════
   No sign-in, no Firestore client. Fetches the deployed snapshot from the
   getCrewCard HTTPS function, renders CrewCardView, then posts one "opened"
   stamp for the recipient key after the card has actually rendered (a link
   scanner fetching the URL never triggers it — the GET is read-only).
*/
import React, { useEffect, useState } from "react";
import CrewCardView from "./CrewCardView.jsx";
import ClientCardView from "./ClientCardView.jsx";

export const FUNCTIONS_BASE = import.meta.env?.VITE_FUNCTIONS_BASE || "https://us-central1-nyc-suite.cloudfunctions.net";
const TOKEN_RE = /^[a-f0-9]{32}$/;

export function parseCardRoute(pathname, search) {
  const m = /^\/card\/([a-f0-9]{32})\/?$/.exec(pathname || "");
  if (!m) return null;
  const r = new URLSearchParams(search || "").get("r") || "";
  return { token: m[1], r: TOKEN_RE.test(r) ? r : "" };
}

function Shell({ children }) {
  return (
    <div style={{ minHeight: "100vh", background: "#F5F3F0", padding: "12px 0 40px" }}>
      {children}
    </div>
  );
}

export default function CrewCardPage({ route }) {
  const [state, setState] = useState({ status: "loading", data: null });

  useEffect(() => {
    document.title = "New York Capture";
    if (!document.querySelector("link[data-crewcard-fonts]")) {
      const l = document.createElement("link");
      l.rel = "stylesheet";
      l.href = "https://fonts.googleapis.com/css2?family=DM+Mono:wght@400;500&family=DM+Sans:wght@300;400;500&display=swap";
      l.setAttribute("data-crewcard-fonts", "1");
      document.head.appendChild(l);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    setState({ status: "loading", data: null });
    (async () => {
      try {
        const res = await fetch(`${FUNCTIONS_BASE}/getCrewCard?token=${route.token}${route.r ? `&r=${route.r}` : ""}`, { cache: "no-store" });
        if (res.status === 404) { if (!cancelled) setState({ status: "gone", data: null }); return; }
        if (!res.ok) throw new Error(`status ${res.status}`);
        const data = await res.json();
        if (!cancelled) { document.title = data?.snapshot?.kind === "client" ? "Session sheet · New York Capture" : "Call sheet · New York Capture"; setState({ status: "ok", data }); }
      } catch (e) {
        if (!cancelled) setState({ status: "error", data: null, message: e.message });
      }
    })();
    return () => { cancelled = true; };
  }, [route.token, route.r]);

  // Stamp "opened" once the card is on screen. Best effort; failures are silent.
  useEffect(() => {
    if (state.status !== "ok" || !route.r) return;
    const t = setTimeout(() => {
      fetch(`${FUNCTIONS_BASE}/openCrewCard`, {
        method: "POST", headers: { "Content-Type": "application/json" }, keepalive: true,
        body: JSON.stringify({ token: route.token, r: route.r }),
      }).catch(() => {});
    }, 800);
    return () => clearTimeout(t);
  }, [state.status, route.token, route.r]);

  if (state.status === "loading") return <Shell><p style={{ textAlign: "center", color: "#888", fontFamily: "system-ui" }}>Loading…</p></Shell>;
  if (state.status === "gone") return (
    <Shell><div style={{ maxWidth: 520, margin: "60px auto", padding: "0 20px", fontFamily: "system-ui", color: "#444", textAlign: "center" }}>
      <h1 style={{ fontWeight: 400, fontSize: 20 }}>This sheet is not available</h1>
      <p>It may have expired or been withdrawn. Check the latest email from New York Capture, or ask Ivin.</p>
    </div></Shell>
  );
  if (state.status === "error") return (
    <Shell><div style={{ maxWidth: 520, margin: "60px auto", padding: "0 20px", fontFamily: "system-ui", color: "#444", textAlign: "center" }}>
      <h1 style={{ fontWeight: 400, fontSize: 20 }}>Could not load the sheet</h1>
      <p>Check your connection and reload. If it keeps failing, reply to the email it came with.</p>
    </div></Shell>
  );
  const { snapshot, version, deployedAt, recipientName } = state.data;
  if (snapshot?.kind === "client") {
    return <Shell><ClientCardView snap={snapshot} token={route.token} version={version} deployedAt={deployedAt} recipientName={recipientName} /></Shell>;
  }
  return <Shell><CrewCardView snap={snapshot} version={version} deployedAt={deployedAt} recipientName={recipientName} /></Shell>;
}
